/**
 * RevisionQueue — sub-tab "Cola de revisión" de Data Health (superadmin only).
 *
 * MVP: sub-tab `Rechazados` funcional. El resto (Low conf / Brand ghost /
 * Manual audit / URLs / Inbox / Brand drift) renderizan badge "próximamente"
 * y delegan al mismo loader (la API ya los soporta — se activan cambiando el
 * scenario; v2 hidratará UI específica por escenario).
 *
 * Spec: docs/pendientes/revision-queue-data-health.md
 * Backend: server/routes/dataHealthRoutes.js (verifyAdmin + requireSuperadmin)
 *
 * Decisiones de implementación:
 *  - Modal nativo (no Radix/HeadlessUI). Mantiene consistency con
 *    HideReasonModal.tsx, RecatalogConfirmModal.tsx (pattern fixed inset-0
 *    z-50 bg-black/40). Evita pull de dependencia nueva. Accesibilidad: ESC
 *    cierra, focus trap mínimo.
 *  - Bulk >20 items requiere check de confirmación + comment opcional.
 *  - Selection state se resetea al cambiar de scenario, no se preserva entre
 *    tabs.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2,
  ExternalLink,
  CheckCircle2,
  Undo2,
  Pencil,
  X,
  Search,
  AlertTriangle,
} from 'lucide-react';
import type { DataHealthItem } from '../../../types/dataHealth';
import { ItemDetailSideDrawer } from '../../common/ItemDetail';
import {
  DataHealthFichaBody,
  DATA_HEALTH_FICHA_TABS,
  HistorialView,
  type FichaTab,
  type FichaHistoryEvent,
} from './DataHealthFicha';
import {
  listRevisionQueue,
  reviveItem,
  confirmRejectItem,
  catalogManualItem,
  bulkRevisionAction,
  getRevisionHistory,
  errorMessage,
  isBackendUnavailable,
  type RevisionScenario,
  type RevisionHistoryEvent,
} from '../../../services/dataHealth';
import { ConfidenceBadge } from '../shared/inspectorTableHelpers';
import DataHealthList, { type DataHealthColumn } from './DataHealthList';
import BulkActionBar from './BulkActionBar';

interface Props {
  clientId: string;
  onToast: (msg: string) => void;
}

interface SubTabDef {
  id: RevisionScenario;
  label: string;
  available: boolean;
}

const SUBTABS: SubTabDef[] = [
  { id: 'rejected', label: 'Rechazados', available: true },
  { id: 'low_conf', label: 'Low conf', available: false },
  { id: 'brand_ghost', label: 'Brand ghost', available: false },
  { id: 'manual_audit', label: 'Manual audit', available: false },
  { id: 'url_unverified', label: 'URLs', available: false },
  { id: 'inbox', label: 'Inbox', available: false },
  { id: 'brand_drift', label: 'Brand drift', available: false },
];

const BULK_CONFIRM_THRESHOLD = 20;

const RevisionQueue: React.FC<Props> = ({ clientId, onToast }) => {
  const [scenario, setScenario] = useState<RevisionScenario>('rejected');
  const [items, setItems] = useState<DataHealthItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [backendDown, setBackendDown] = useState(false);

  const [search, setSearch] = useState('');
  const [marca, setMarca] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [drawerItem, setDrawerItem] = useState<DataHealthItem | null>(null);
  const [bulkModalAction, setBulkModalAction] = useState<'revive' | 'confirm_reject' | null>(null);
  const [manualModalItem, setManualModalItem] = useState<DataHealthItem | null>(null);

  // Guard anti-race: solo la respuesta más nueva escribe estado (T-B).
  const reqIdRef = useRef(0);

  const loadData = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    setBackendDown(false);
    try {
      const r = await listRevisionQueue(clientId, scenario, {
        page,
        pageSize,
        search: search.trim() || undefined,
        marca: marca.trim() || undefined,
      });
      if (myReq !== reqIdRef.current) return; // respuesta stale
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      if (isBackendUnavailable(e)) setBackendDown(true);
      else setErr(errorMessage(e));
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [clientId, scenario, page, pageSize, search, marca]);

  useEffect(() => { void loadData(); }, [loadData]);

  useEffect(() => {
    setSelected(new Set());
    setPage(1);
  }, [scenario]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageAllSelected = useMemo(
    () => items.length > 0 && items.every((it) => selected.has(it.id_catalogo)),
    [items, selected]
  );

  const togglePageAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (pageAllSelected) items.forEach((it) => next.delete(it.id_catalogo));
      else items.forEach((it) => next.add(it.id_catalogo));
      return next;
    });
  };
  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Handlers de acción single-item desde drawer.
  const handleRevive = async (item: DataHealthItem, comment: string) => {
    try {
      await reviveItem(clientId, item.id_catalogo, { scenario, comment });
      onToast(`Revivido: ${item.id_catalogo}`);
      setDrawerItem(null);
      await loadData();
    } catch (e) { onToast(`Error: ${errorMessage(e)}`); }
  };
  const handleConfirmReject = async (item: DataHealthItem, comment: string) => {
    try {
      await confirmRejectItem(clientId, item.id_catalogo, { scenario, comment });
      onToast(`Rechazo confirmado: ${item.id_catalogo}`);
      setDrawerItem(null);
      await loadData();
    } catch (e) { onToast(`Error: ${errorMessage(e)}`); }
  };

  const handleBulkSubmit = async (
    action: 'revive' | 'confirm_reject',
    comment: string,
    confirmed: boolean
  ) => {
    const ids = Array.from(selected);
    try {
      const r = await bulkRevisionAction(clientId, { ids, action, scenario, comment, confirmed });
      if (r.confirm_required) {
        onToast(`Se requiere confirmación (${r.count} items)`);
        return false;
      }
      onToast(`Bulk ${action}: ${r.processed || 0} procesados`);
      setBulkModalAction(null);
      setSelected(new Set());
      await loadData();
      return true;
    } catch (e) {
      onToast(`Error bulk: ${errorMessage(e)}`);
      return false;
    }
  };

  // Config de columnas para DataHealthList. Anchos FIJOS (table-layout:fixed):
  // title es la columna flexible (sin width). Mismo lenguaje visual que OCULTOS.
  const columns: DataHealthColumn<DataHealthItem>[] = [
    {
      key: 'id_catalogo',
      header: 'id_catalogo',
      width: '7rem',
      cellClassName: 'font-mono text-[11px]',
      render: (it) => it.id_catalogo,
    },
    {
      key: 'title',
      header: 'title',
      render: (it) => (
        <span className="block truncate" title={it.title || ''}>
          {it.title || <span className="text-gray-300">—</span>}
        </span>
      ),
    },
    {
      key: 'sitio',
      header: 'sitio',
      width: '6rem',
      render: (it) => it.sitio || <span className="text-gray-300">—</span>,
    },
    {
      // MARCA: SOLO el valor GOLD (catalog_brand con fallback a la marca de la
      // toma). Sin flecha toma→catalog. null en color distintivo (text-red-400).
      key: 'marca',
      header: 'marca',
      width: '6rem',
      render: (it) =>
        it.catalog_brand || it.marca || <span className="text-red-400 italic">null</span>,
    },
    {
      key: 'cat',
      header: 'cat',
      width: '6rem',
      render: (it) => it.catalog_category || <span className="text-red-400 italic">null</span>,
    },
    {
      key: 'sub',
      header: 'sub',
      width: '6rem',
      render: (it) => it.subcategory || <span className="text-red-400 italic">null</span>,
    },
    {
      key: 'conf',
      header: 'conf',
      width: '4rem',
      render: (it) => <ConfidenceBadge value={it.confidence_score ?? null} />,
    },
    {
      key: 'visibility',
      header: 'visibility',
      width: '5rem',
      render: (it) =>
        it.visibility === 0 ? (
          <span className="inline-block text-[10px] bg-gray-200 text-gray-700 rounded px-1.5 py-0.5">hidden</span>
        ) : (
          <span className="inline-block text-[10px] bg-green-100 text-green-700 rounded px-1.5 py-0.5">visible</span>
        ),
    },
    {
      key: 'url',
      header: '',
      width: '3rem',
      align: 'center',
      render: (it) =>
        it.url ? (
          <a
            href={String(it.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="w-3 h-3 inline" />
          </a>
        ) : null,
    },
  ];

  return (
    <div className="space-y-3">
      {/* Subtabs por escenario */}
      <div className="flex items-center gap-1 border-b border-gray-200 overflow-x-auto">
        {SUBTABS.map((t) => {
          const active = scenario === t.id;
          const disabled = !t.available;
          return (
            <button
              key={t.id}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && setScenario(t.id)}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1 ${
                active
                  ? 'border-blue-700 text-blue-700 bg-blue-50'
                  : disabled
                    ? 'border-transparent text-gray-300 cursor-not-allowed'
                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
              title={disabled ? 'Próximamente' : ''}
            >
              {t.label}
              {!t.available && (
                <span className="text-[9px] uppercase bg-gray-200 text-gray-500 rounded px-1 ml-0.5">soon</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            placeholder="Buscar (título o marca)"
            className="pl-7 pr-2 py-1 text-xs border border-gray-300 rounded w-64 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <input
          type="text"
          value={marca}
          onChange={(e) => { setPage(1); setMarca(e.target.value); }}
          placeholder="Marca"
          className="px-2 py-1 text-xs border border-gray-300 rounded w-32 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <div className="text-xs text-gray-500 ml-auto tabular-nums">
          {loading ? '...' : `${total.toLocaleString('es-AR')} items`}
        </div>
      </div>

      {/* Estados */}
      {backendDown && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
          Backend Cola de Revisión pendiente de deploy. La UI está lista pero la API responde 404.
        </div>
      )}
      {err && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      {/* Listado unificado (DataHealthList) con selección múltiple */}
      <DataHealthList<DataHealthItem>
        columns={columns}
        rows={items}
        getRowId={(it) => it.id_catalogo}
        onRowClick={(it) => setDrawerItem(it)}
        activeRowId={drawerItem?.id_catalogo ?? null}
        loading={loading}
        emptyState={<span className="italic">Sin items para este escenario.</span>}
        selection={{
          selected,
          pageAllSelected,
          onTogglePage: () => togglePageAll(),
          onToggleOne: (id) => toggleOne(id),
        }}
        pagination={{
          idPrefix: 'cola',
          pageIndex: page - 1,
          pageSize,
          totalRows: total,
          totalPages,
          onPageChange: (newIndex) => setPage(newIndex + 1),
          onPageSizeChange: (newSize) => {
            setPageSize(newSize);
            setPage(1);
          },
        }}
      />

      {/* Bulk action bar — reusa el componente compartido de Data Health (mismo
          que Ocultos/Dudosos), en vez de una barra sticky ad-hoc. */}
      <BulkActionBar
        selectedCount={selected.size}
        onClear={() => setSelected(new Set())}
        actions={[
          {
            id: 'revive',
            label: 'Revivir',
            icon: Undo2,
            variant: 'success',
            onClick: () => setBulkModalAction('revive'),
          },
          {
            id: 'confirm_reject',
            label: 'Confirmar rechazo',
            icon: CheckCircle2,
            variant: 'warning',
            onClick: () => setBulkModalAction('confirm_reject'),
          },
        ]}
      />

      {/* Drawer detalle */}
      {drawerItem && (
        <RevisionDrawer
          clientId={clientId}
          item={drawerItem}
          scenario={scenario}
          onClose={() => setDrawerItem(null)}
          onRevive={(c) => handleRevive(drawerItem, c)}
          onConfirmReject={(c) => handleConfirmReject(drawerItem, c)}
          onCatalogManual={() => setManualModalItem(drawerItem)}
          onPrev={() => {
            const i = items.findIndex((it) => it.id_catalogo === drawerItem.id_catalogo);
            if (i > 0) setDrawerItem(items[i - 1]);
          }}
          onNext={() => {
            const i = items.findIndex((it) => it.id_catalogo === drawerItem.id_catalogo);
            if (i >= 0 && i < items.length - 1) setDrawerItem(items[i + 1]);
          }}
          hasPrev={items.findIndex((it) => it.id_catalogo === drawerItem.id_catalogo) > 0}
          hasNext={(() => {
            const i = items.findIndex((it) => it.id_catalogo === drawerItem.id_catalogo);
            return i >= 0 && i < items.length - 1;
          })()}
        />
      )}

      {/* Bulk modal */}
      {bulkModalAction && (
        <BulkActionModal
          action={bulkModalAction}
          count={selected.size}
          onClose={() => setBulkModalAction(null)}
          onSubmit={(comment, confirmed) => handleBulkSubmit(bulkModalAction, comment, confirmed)}
        />
      )}

      {/* Manual catalog modal */}
      {manualModalItem && (
        <CatalogManualModal
          item={manualModalItem}
          scenario={scenario}
          onClose={() => setManualModalItem(null)}
          onSubmit={async (fields, comment) => {
            try {
              await catalogManualItem(clientId, manualModalItem.id_catalogo, { scenario, comment, fields });
              onToast(`Catalogado manual: ${manualModalItem.id_catalogo}`);
              setManualModalItem(null);
              setDrawerItem(null);
              await loadData();
            } catch (e) {
              onToast(`Error: ${errorMessage(e)}`);
            }
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// Drawer detalle — muestra item + 3 botones acción + tab Historial
// ============================================================================

interface DrawerProps {
  clientId: string;
  item: DataHealthItem;
  scenario: RevisionScenario;
  onClose: () => void;
  onRevive: (comment: string) => Promise<void>;
  onConfirmReject: (comment: string) => Promise<void>;
  onCatalogManual: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

/**
 * RevisionDrawer — ficha de la Cola de revisión. Reusa el cuerpo COMPARTIDO
 * (DataHealthFichaBody) con las 3 tabs Resumen/Enriquecimiento/Historial —
 * idéntica a Ocultos (DataHealthRowDrawer) — pero en MODO 3
 * (ItemDetailSideDrawer, overlay full-screen ~30%) y con una barra de acciones
 * propia (Revivir / Confirmar rechazo / Catalogar manual) en el footer.
 */
/**
 * Modos de acción del footer del drawer. Principio UX (Gabriel): UNA sola acción
 * primaria visible — mode-selector (segmented) + 1 botón cuyo label/color cambia
 * según el modo. Se esconden los controles irrelevantes al modo activo:
 *  - 'revive' / 'confirm_reject' → muestran el textarea de comentario y ejecutan
 *    la acción con ese comment.
 *  - 'manual' → NO muestra textarea (el modal de Catalogar manual trae su propio
 *    comentario); el botón primario abre ese modal.
 * 'revive' sólo está disponible si el item está oculto (visibility===0).
 */
type ActionMode = 'revive' | 'confirm_reject' | 'manual';

const ACTION_MODES: {
  id: ActionMode;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  btnClass: string;
}[] = [
  { id: 'revive', label: 'Revivir', icon: Undo2, btnClass: 'bg-green-600 hover:bg-green-700' },
  { id: 'confirm_reject', label: 'Confirmar rechazo', icon: CheckCircle2, btnClass: 'bg-amber-600 hover:bg-amber-700' },
  { id: 'manual', label: 'Catalogar manual', icon: Pencil, btnClass: 'bg-blue-600 hover:bg-blue-700' },
];

const RevisionDrawer: React.FC<DrawerProps> = ({
  clientId, item, scenario, onClose, onRevive, onConfirmReject, onCatalogManual,
  onPrev, onNext, hasPrev, hasNext,
}) => {
  const [tab, setTab] = useState<FichaTab>('resumen');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 'revive' sólo aplica a items ocultos; si el item está visible arrancamos en
  // 'confirm_reject' para no ofrecer una acción imposible como default.
  const reviveDisabled = item.visibility !== 0;
  const [mode, setMode] = useState<ActionMode>(reviveDisabled ? 'confirm_reject' : 'revive');
  // Si cambia el item (prev/next) y el modo actual no aplica, corregir.
  useEffect(() => {
    if (mode === 'revive' && reviveDisabled) setMode('confirm_reject');
  }, [reviveDisabled, mode]);

  const active = ACTION_MODES.find((m) => m.id === mode)!;
  const ActiveIcon = active.icon;
  const showComment = mode !== 'manual';

  const runPrimary = async () => {
    if (mode === 'manual') {
      onCatalogManual();
      return;
    }
    setSubmitting(true);
    try {
      await (mode === 'revive' ? onRevive(comment) : onConfirmReject(comment));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ItemDetailSideDrawer
      open
      title={`Catalog ID: ${item.id_catalogo}`}
      headerActions={
        <span className="text-[10px] uppercase bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">
          {scenario}
        </span>
      }
      tabs={DATA_HEALTH_FICHA_TABS}
      activeTab={tab}
      onTabChange={(id) => setTab(id as FichaTab)}
      onClose={onClose}
      onPrev={onPrev}
      onNext={onNext}
      hasPrev={hasPrev}
      hasNext={hasNext}
      footer={
        <div className="space-y-2">
          {/* Mode-selector segmentado: elige QUÉ acción hacer */}
          <div className="inline-flex rounded border border-gray-200 overflow-hidden text-xs">
            {ACTION_MODES.map((m) => {
              const isActive = m.id === mode;
              const disabled = m.id === 'revive' && reviveDisabled;
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  type="button"
                  disabled={disabled || submitting}
                  onClick={() => setMode(m.id)}
                  title={disabled ? 'Solo disponible para items ocultos' : ''}
                  className={`flex items-center gap-1 px-2.5 py-1 border-r border-gray-200 last:border-r-0 transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : disabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" /> {m.label}
                </button>
              );
            })}
          </div>

          {/* Comentario: sólo para revivir/confirmar (manual trae el suyo) */}
          {showComment && (
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Comentario (opcional)…"
              rows={2}
              maxLength={1000}
              className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          )}

          {/* UNA sola acción primaria: label + color cambian según el modo */}
          <div className="flex justify-end">
            <button
              type="button"
              disabled={submitting}
              onClick={runPrimary}
              className={`text-xs text-white disabled:opacity-40 rounded px-3 py-1.5 flex items-center gap-1 ${active.btnClass}`}
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ActiveIcon className="w-3.5 h-3.5" />}
              {active.label}
            </button>
          </div>
        </div>
      }
    >
      <DataHealthFichaBody
        clientId={clientId}
        item={item}
        tab={tab}
        historial={<RevisionHistorial clientId={clientId} idCatalogo={item.id_catalogo} />}
      />
    </ItemDetailSideDrawer>
  );
};

// ----------------------------------------------------------------------------
// Historial (Cola): carga vía getRevisionHistory y adapta a FichaHistoryEvent —
// misma presentación (HistorialView) que Ocultos.
// ----------------------------------------------------------------------------

const RevisionHistorial: React.FC<{ clientId: string; idCatalogo: string }> = ({ clientId, idCatalogo }) => {
  const [events, setEvents] = useState<RevisionHistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [backendDown, setBackendDown] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setBackendDown(false);
    getRevisionHistory(clientId, idCatalogo)
      .then((r) => {
        if (!cancelled) setEvents(r.events);
      })
      .catch((e) => {
        if (cancelled) return;
        if (isBackendUnavailable(e)) setBackendDown(true);
        else setErr(errorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, idCatalogo]);

  const adapted: FichaHistoryEvent[] = useMemo(
    () =>
      events.map((ev) => ({
        id: `${ev.kind}-${ev.id}`,
        label: ev.kind === 'feedback' ? `Feedback · ${ev.decision ?? ''}`.trim() : (ev.event_type || 'evento'),
        kind: ev.kind === 'feedback' ? ('feedback' as const) : ('generic' as const),
        actor: (ev.kind === 'feedback' ? ev.reviewer : ev.actor) || '—',
        at: ev.at,
        detail: <RevisionEventPayload ev={ev} />,
      })),
    [events]
  );

  return <HistorialView events={adapted} loading={loading} error={err} backendDown={backendDown} />;
};

const RevisionEventPayload: React.FC<{ ev: RevisionHistoryEvent }> = ({ ev }) => {
  if (ev.kind === 'feedback') {
    return ev.comment ? <div className="text-gray-600 italic">"{ev.comment}"</div> : null;
  }
  if (ev.payload && Object.keys(ev.payload).length > 0) {
    return (
      <pre className="text-[10px] text-gray-600 whitespace-pre-wrap break-all">
        {JSON.stringify(ev.payload, null, 2)}
      </pre>
    );
  }
  return null;
};

// ============================================================================
// Bulk action modal
// ============================================================================

interface BulkProps {
  action: 'revive' | 'confirm_reject';
  count: number;
  onClose: () => void;
  onSubmit: (comment: string, confirmed: boolean) => Promise<boolean>;
}

const BulkActionModal: React.FC<BulkProps> = ({ action, count, onClose, onSubmit }) => {
  const [comment, setComment] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const needsConfirm = count > BULK_CONFIRM_THRESHOLD;
  const label = action === 'revive' ? 'Revivir' : 'Confirmar rechazo';
  const canSubmit = !needsConfirm || confirmed;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">{label} {count} items</h3>
          <button onClick={onClose} disabled={submitting} className="p-1 text-gray-500 hover:text-gray-800">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3 text-xs">
          {needsConfirm && (
            <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                Vas a aplicar la acción sobre <strong>{count}</strong> items. Confirmá la operación.
              </div>
            </div>
          )}
          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">Comentario (opcional)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={1000}
              className="w-full text-xs border border-gray-300 rounded px-2 py-1"
            />
          </div>
          {needsConfirm && (
            <label className="flex items-center gap-2 text-gray-700">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              <span>Confirmo la acción sobre {count} items</span>
            </label>
          )}
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-xs px-3 py-1.5 rounded text-gray-600 hover:bg-gray-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={submitting || !canSubmit}
            onClick={async () => {
              setSubmitting(true);
              try { await onSubmit(comment, confirmed); } finally { setSubmitting(false); }
            }}
            className={`text-xs px-3 py-1.5 rounded text-white flex items-center gap-1 disabled:opacity-40 ${
              action === 'revive' ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'
            }`}
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            {label}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Manual catalog modal
// ============================================================================

interface CatalogManualFields {
  catalog_brand?: string | null;
  catalog_category?: string | null;
  subcategory?: string | null;
  segmento?: string | null;
}

interface ManualProps {
  item: DataHealthItem;
  scenario: RevisionScenario;
  onClose: () => void;
  onSubmit: (fields: CatalogManualFields, comment: string) => Promise<void>;
}

const CatalogManualModal: React.FC<ManualProps> = ({ item, onClose, onSubmit }) => {
  const [brand, setBrand] = useState(item.catalog_brand || '');
  const [cat, setCat] = useState(item.catalog_category || '');
  const [sub, setSub] = useState(item.subcategory || '');
  const [seg, setSeg] = useState(item.segmento || '');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ESC cierra — consistente con el drawer (ItemDetailSideDrawer) y los otros
  // modales del panel. No cerramos mientras se envía.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="bg-white rounded-lg max-w-lg w-full mx-4 shadow-xl border border-gray-200">
        {/* Header — mismo lenguaje que BulkActionModal / los modales del panel */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Pencil className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">Catalogar manualmente</h3>
              <p className="text-xs text-gray-500 truncate" title={item.title || ''}>
                {item.title || <span className="text-gray-300">Sin título</span>}
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={submitting} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 text-xs">
          {/* Contexto: id + política manually_edited_fields */}
          <div className="bg-gray-50 border border-gray-200 rounded p-2.5 text-[11px] text-gray-600">
            <span className="text-gray-500">Catalog ID:</span>{' '}
            <span className="font-mono text-gray-900">{item.id_catalogo}</span>
            <div className="mt-1 flex items-start gap-1">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-500" />
              <span>
                Los campos editados pasan a{' '}
                <span className="font-medium">manually_edited_fields</span> y no son sobrescritos
                por el LLM en runs futuros.
              </span>
            </div>
          </div>

          {/* Parámetros del árbol — labels humanos + hint del campo técnico */}
          <div className="grid grid-cols-2 gap-3">
            <LabelInput label="Marca" hint="catalog_brand" value={brand} onChange={setBrand} />
            <LabelInput label="Categoría" hint="catalog_category" value={cat} onChange={setCat} />
            <LabelInput label="Subcategoría" hint="subcategory" value={sub} onChange={setSub} />
            <LabelInput label="Segmento" hint="segmento" value={seg} onChange={setSeg} />
          </div>

          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">Comentario (opcional)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              maxLength={1000}
              className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={submitting} className="text-xs px-3 py-1.5 rounded text-gray-600 hover:bg-gray-100">
            Cancelar
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={async () => {
              setSubmitting(true);
              try {
                const fields: CatalogManualFields = {};
                if (brand !== (item.catalog_brand || '')) fields.catalog_brand = brand || null;
                if (cat !== (item.catalog_category || '')) fields.catalog_category = cat || null;
                if (sub !== (item.subcategory || '')) fields.subcategory = sub || null;
                if (seg !== (item.segmento || '')) fields.segmento = seg || null;
                if (Object.keys(fields).length === 0) {
                  onClose();
                  return;
                }
                await onSubmit(fields, comment);
              } finally {
                setSubmitting(false);
              }
            }}
            className="text-xs px-3 py-1.5 rounded text-white bg-blue-600 hover:bg-blue-700 flex items-center gap-1 disabled:opacity-40"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Mini components
// ============================================================================

/**
 * Campo de parámetro del árbol. Label humano (Marca/Categoría…) + hint opcional
 * con el nombre técnico del campo (catalog_brand…) para que el reviewer entienda
 * a qué columna mapea. Mismo estilo de input que el resto del config.
 */
const LabelInput: React.FC<{ label: string; hint?: string; value: string; onChange: (v: string) => void }> = ({
  label,
  hint,
  value,
  onChange,
}) => (
  <div>
    <label className="flex items-baseline gap-1 mb-1">
      <span className="text-[11px] font-medium text-gray-700">{label}</span>
      {hint && <span className="text-[9px] font-mono text-gray-400">{hint}</span>}
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
    />
  </div>
);

export default RevisionQueue;
